import { FormItem, TextAreaInput, useField } from '@framework/form';
import { RowElement, StyledDrawerCollapse } from '@framework/ui';
import { yup } from '@framework/validation';
import { Card, Collapse } from 'antd';
import { mapValues } from 'lodash';
import React, { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import {
  useAsyncServiceEngineContext,
  useTreeReferencesContext,
} from '../../../../../tree/references';
import { Datatype } from '../../../../expressions/datatype/Datatype';
import { DefaultAssignableExpressionSchema } from '../../../../expressions/repository/assignable/AssignableExpressionSchema';
import { IAssignableExpression } from '../../../../expressions/repository/assignable/IAssignableExpression';
import { IAsyncServiceParameter } from '../../../../expressions/repository/async/context/IAsyncService';
import { IWidgetComponentProps } from '../../../IWidgetType';
import { WidgetType } from '../../../Widget';
import { BaseLayoutSchema } from '../../common/BaseLayoutSchema';
import { LayoutHideConfigInput } from '../../common/config/LayoutHideConfigInput';
import { LayoutRemoveBorderConfigInput } from '../../common/config/LayoutRemoveBorderConfigInput';
import { AsyncServiceInputConfigForm } from '../async-select/components/AsyncServiceInputConfigForm';
import { ServiceOutputSelect } from '../async-select/components/ServiceOutputSelect';
import { ServiceSelectorInput } from '../async-select/components/ServiceSelectorInput';
import {
  CommonConfigComponent,
  CommonInputWidgetConfigSchema,
} from '../common/CommonInputConfigComponent';
import { asyncValueWidgetObjectName } from './AsyncValueWidget.type';
import { IAsyncValueWidget } from './IAsyncValueWidget';

const { Panel } = Collapse;

export const AsyncValueConfigSchema: yup.ObjectSchema<IAsyncValueWidget> =
  CommonInputWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetType.AsyncValue).required(),
    inputs: yup.lazy(obj => {
      return yup
        .object<Record<string, IAssignableExpression>>(
          mapValues(
            obj ?? {},
            (value, key) => DefaultAssignableExpressionSchema,
          ),
        )
        .required()
        .label('Parametri');
    }),
    outputs: yup
      .array(yup.object() as yup.ObjectSchema<IAsyncServiceParameter>)
      .label('Risultati')
      .optional(),
    dataType: yup
      .string()
      .oneOf([
        Datatype.String,
        Datatype.Boolean,
        Datatype.Number,
        Datatype.String,
      ])
      .label('Tipo di dato')
      .required(),
    serviceId: yup.string().required().label('Servizio'),
    labelFields: yup
      .array(yup.string().required())
      .required()
      .default([])
      .min(1, 'Deve essere presente almeno un campo')
      .label('Etichetta Valore'),
    valueField: yup.string().required().label('Valore'),
    notes: yup.string().notRequired().label('Note'),

    layout: BaseLayoutSchema.shape({
      hide: yup.boolean().required().default(false).label('Nascondi elemento'),
      removeBorder: yup
        .boolean()
        .required()
        .default(false)
        .label('Rimuovi bordo'),
    }),
  })
    .required()
    .noUnknown();

export interface IAsyncValueConfigComponentProps
  extends IWidgetComponentProps<IAsyncValueWidget> {}

export function AsyncValueConfigComponent(
  props: IAsyncValueConfigComponentProps,
) {
  const { references: treeReferences } = useTreeReferencesContext();
  const { services } = useAsyncServiceEngineContext();

  const selectedId = useWatch({ name: `serviceId` });
  const selected = services.find(s => s.id === selectedId);

  const { setValue: setLabelFields } = useField('labelFields');
  const { setValue: setValueField } = useField('valueField');
  const { setValue: setInputsField } = useField('inputs');
  const { setValue: setOutputsField } = useField('outputs');

  const { setValue: setDataTypeField } = useField('dataType');

  const hasInputs = selected?.inputs && selected?.inputs.length > 0;

  // Rimuovo le auto-reference
  const code = useWatch({ name: 'code' });
  const references = useMemo(() => {
    return treeReferences.filter(
      r =>
        r.code !== code &&
        (!r.path ||
          r.path.every(p => p.code !== asyncValueWidgetObjectName(code))),
    );
  }, [code, treeReferences]);

  return (
    <StyledDrawerCollapse defaultActiveKey={['general', 'service']} ghost>
      <Panel header="Impostazioni Generali" key="general">
        <CommonConfigComponent {...props} hideConfigDefaultValue />
        <FormItem
          name="notes"
          label="Note"
          help="Questo testo sarà visibile solo in fase di configurazione"
        >
          <TextAreaInput name="notes" rows={2} />
        </FormItem>
      </Panel>

      <Panel header="Impostazioni Servizio" key="service">
        <ServiceSelectorInput
          onChange={(value, option, onChange) => {
            onChange(value);
            setLabelFields([]);
            setValueField(null);
            setDataTypeField(null);
            setInputsField({});
            setOutputsField((option as any).service.outputs);
          }}
          name="serviceId"
        />
        {selected && (
          <>
            <div style={{ paddingTop: '8px' }}>
              <Card size="small" title="Configura struttura">
                <FormItem
                  name="labelFields"
                  help="Se vengono selezionati più campi, saranno mostrati separati da un '-' nell'ordine configurato"
                >
                  <ServiceOutputSelect
                    label="Etichetta"
                    service={selected}
                    name="labelFields"
                    mode="multiple"
                  />
                </FormItem>
                <FormItem name="valueField">
                  <ServiceOutputSelect
                    label="Valore"
                    service={selected}
                    name="valueField"
                    // Imposto il dataype del widget a quello del tipo di risultato ottenuto dalla chiamata
                    onChange={(value, option, onChange) => {
                      onChange(value ?? null);
                      if (Array.isArray(option)) return;
                      setDataTypeField(option?.context.type.type);
                    }}
                  />
                </FormItem>
              </Card>
            </div>

            {hasInputs && (
              <AsyncServiceInputConfigForm
                name="inputs"
                service={selected}
                references={references}
              />
            )}
          </>
        )}

        <RowElement>
          <LayoutHideConfigInput />
          <LayoutRemoveBorderConfigInput />
        </RowElement>
      </Panel>
    </StyledDrawerCollapse>
  );
}
